/**
 * Consolidated helpers for working with relation objects and friendly names
 * Used by both grouping and export logic
 */
import { HUB_FOREIGN_KEY_MAP } from "@/lib/schema/foreign-key-map";

/**
 * Map of field IDs to their possible nested relation field names
 */
const RELATION_FIELD_MAP: Record<string, string[]> = {
  company_id: ["companies"],
  client_id: ["companies"],
  ticket_id: ["tickets"],
  user_id: ["app_users", "users"],
  assigned_to: ["app_users", "users"],
  created_by: ["app_users", "users"],
  contact_id: ["contacts"],
};

const METADATA_RELATION_FIELD_MAP: Record<string, string[]> = Object.entries(HUB_FOREIGN_KEY_MAP).reduce(
  (acc, [tableField, config]) => {
    const [, fieldId] = tableField.split(".");
    if (!fieldId) return acc;
    const relationField = config.referencedTable;
    const next = acc[fieldId] ?? [];
    if (!next.includes(relationField)) {
      acc[fieldId] = [...next, relationField];
    }
    return acc;
  },
  {} as Record<string, string[]>
);

type RelationObject = Record<string, unknown> & { id: unknown };

function isRelationObject(value: unknown): value is RelationObject {
  return typeof value === "object" && value !== null && "id" in value;
}

/**
 * Extract a friendly name from a relation object
 * This is completely generic and works for any entity type
 */
export function extractFriendlyName(value: unknown, fieldId: string): string {
  if (!value) return "";

  // If it's already a string, return as is
  if (typeof value === "string") {
    return value;
  }

  // Check if it's a relation object (has an id property and other fields)
  if (isRelationObject(value)) {
    // Special handling for tickets: combine ticket_number and title
    if (value.ticket_number && value.title) {
      return `#${value.ticket_number}: ${value.title}`;
    }

    // Special handling for users: combine first_name and last_name
    if (value.first_name || value.last_name) {
      const parts = [value.first_name, value.last_name].filter(Boolean);
      if (parts.length > 0) {
        return parts.join(" ");
      }
    }

    // Generic fallback for any object with common display fields
    // This handles all entities including companies, contacts, tasks, etc.
    const displayFields = ["name", "label", "title", "email", "display_name", "full_name"];
    for (const field of displayFields) {
      if (value[field]) {
        return String(value[field]);
      }
    }

    // Return the id if no friendly name found
    return String(value.id);
  }

  // Return as string
  return String(value || "");
}

/**
 * Get the actual relation value from an item
 * Handles both direct relation objects and UUIDs with nested relation objects
 */
export function getRelationValue(item: Record<string, unknown>, fieldId: string): unknown {
  const value = item[fieldId];

  // If the value is already an object with an id, it's a relation object - return it
  if (isRelationObject(value)) {
    return value;
  }

  // If it's a string/UUID, check if there's a nested relation object
  if (typeof value === "string" || !value) {
    const metadataRelationFields = METADATA_RELATION_FIELD_MAP[fieldId] || [];
    const fallbackRelationFields = RELATION_FIELD_MAP[fieldId] || [];
    const possibleFields = [...new Set([...metadataRelationFields, ...fallbackRelationFields])];
    for (const field of possibleFields) {
      const nested = item[field];
      if (isRelationObject(nested)) {
        return nested;
      }
    }
  }

  return value;
}

/**
 * Transform data to extract friendly names from relation objects
 */
export function transformDataForExport(data: Record<string, unknown>[]): Record<string, unknown>[] {
  return data.map((item) => {
    const transformed: Record<string, unknown> = { ...item };

    // Check each field for relation objects or UUIDs
    Object.keys(transformed).forEach((fieldId) => {
      const value = transformed[fieldId];

      // Check if it's an object with an id property
      if (isRelationObject(value)) {
        transformed[fieldId] = extractFriendlyName(value, fieldId);
      }
      // Check if it's a string UUID and there's a nested relation object
      else if (typeof value === "string" && value.length > 20) {
        const relationValue = getRelationValue(item, fieldId);
        if (isRelationObject(relationValue)) {
          transformed[fieldId] = extractFriendlyName(relationValue, fieldId);
        }
      }
    });

    return transformed;
  });
}
